/**
 * The ONE place a column's display label is swapped for the firm's word.
 *
 * Spec 061 §4 enumerates eight surfaces that must each show the alias — grid
 * header, filter-bar label, Group By list, tooltips, aria-labels, saved views,
 * client exports, the AI grounding payload — and reads as eight wiring jobs.
 * Read from source they are not: every one of them takes its text from
 * `col.label` on the *same* column object (spec 073 §3). So this is a single
 * transform applied where the column list is built, and the surfaces inherit it.
 *
 * That is also why this must NOT be applied twice on one path. `rawLabel` is
 * the guard: it is set on first application and never overwritten, so the
 * original CB label survives however many times a column passes through.
 *
 * ## What is deliberately never touched
 *
 * `columnId` — the identity, the wire key, and the key every saved view and
 * every stored preference is stored under. Renaming is cosmetic; the wire
 * contract is not. The dominant report bug on this project is a misnamed key
 * binding to nothing: CB's Newtonsoft deserialization silently ignores unknown
 * properties, the stored proc runs UNSCOPED, and nothing errors — not the
 * screen, not the logs, not the tests. An alias must never reach a filter key.
 *
 * ## cb2 never invents a label
 *
 * No title-casing, no dotted-path cleanup, no trimming of CB's own text. The
 * only label cb2 may ever display other than CB's is one a firm admin
 * explicitly typed. A blank or whitespace-only alias is therefore not a label —
 * it is the absence of one, and CB's own wins.
 */

/** True when `alias` is a real firm-typed label rather than an empty cell. */
const usable = (alias) => typeof alias === 'string' && alias.trim().length > 0

/**
 * Apply the firm's aliases to a column list.
 *
 * @param {Array<object>} columns  CB's columns, untouched on return if nothing applies
 * @param {Record<string,string>} aliases  `{ [columnId]: alias }`
 * @returns {Array<object>} the same array reference when no alias applies
 */
export const aliasColumns = (columns, aliases) => {
  if (!Array.isArray(columns) || !columns.length) return columns
  if (!aliases || !Object.keys(aliases).length) return columns

  let changed = false
  const next = columns.map((col) => {
    const alias = aliases[String(col?.columnId)]
    if (!usable(alias) || alias === col.label) return col
    changed = true
    // rawLabel is set ONCE. The ⓘ tooltip needs it to say 'originally "…"',
    // and drift detection compares it against the `original` the backend
    // stored at rename time. Re-deriving it here would launder a stale label
    // into the new original and silently disarm drift detection.
    return { ...col, label: alias, rawLabel: col.rawLabel ?? col.label }
  })

  // Preserve referential identity when nothing applied — these lists feed
  // `useMemo` dependency arrays and a `columns` memo all over the grid, and a
  // fresh array every render is how the ticking-value bugs happened.
  return changed ? next : columns
}

/**
 * Apply aliases to a whole report definition, leaving every other field alone.
 * Returns the same object reference when nothing applies.
 */
export const aliasDefinition = (definition, aliases) => {
  if (!definition) return definition
  const columns = aliasColumns(definition.columns, aliases)
  return columns === definition.columns ? definition : { ...definition, columns }
}

/**
 * Has CB's own label moved since the admin renamed this column?
 *
 * The admin renamed something that said `original`. If CB now calls it
 * something else, the alias may be hiding a column whose *meaning* changed —
 * which on a financial report is worth surfacing rather than swallowing.
 */
export const hasDrifted = (column, metaEntry) => {
  const original = metaEntry?.original
  if (!original || !column) return false
  const current = column.rawLabel ?? column.label
  return typeof current === 'string' && current !== original
}

export default aliasColumns
